import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useRef } from 'react';
import { HookformModalEntry } from '../modal-manager.state';
import { HookformCompletion, HookformFooterFormApi, ModalResult } from '../modal-manager.types';
import { ModalFrame } from './ModalFrame';
import { isValidationProblemDetails } from 'src/helpers/error-helpers';
import { useTranslation } from 'react-i18next';
import { useHookformValidationErrorHandler } from './useHookformValidationErrorHandler';

type HookformModalProps<T extends FieldValues> = {
  entry: HookformModalEntry<T>;
  stackIndex: number;
  isTopmost: boolean;
  onResolve: (modalId: string, result: HookformCompletion<T>) => void;
};

export const HookformModal = <T extends FieldValues>({
  entry,
  stackIndex,
  isTopmost,
  onResolve,
}: HookformModalProps<T>) => {
  const { t } = useTranslation();
  const resolveAnimationComplete = useRef<() => void>();
  const form = useForm<T>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    ...entry.options.formProps,
  });
  const formApi: HookformFooterFormApi<T> = {
    handleSubmit: form.handleSubmit,
    setError: form.setError,
    clearErrors: form.clearErrors,
    getValues: form.getValues,
    formState: {
      isSubmitting: form.formState.isSubmitting,
      errors: form.formState.errors,
      isValid: form.formState.isValid,
    },
  };
  const confirm = useCallback(
    (payload: T) => onResolve(entry.modalId, { result: ModalResult.Confirm, payload }),
    [entry.modalId, onResolve],
  );
  const cancel = () => onResolve(entry.modalId, { result: ModalResult.Cancel });
  const close = () => onResolve(entry.modalId, { result: ModalResult.Closed });
  const handleValidationError = useHookformValidationErrorHandler(form);

  const formDisabled = form.formState.isSubmitting || (form.formState.isSubmitted && form.formState.isSubmitSuccessful);

  return (
    <FormProvider {...form}>
      <ModalFrame
        title={entry.options.title}
        size={entry.options.size}
        stackIndex={stackIndex}
        isTopmost={isTopmost}
        onClose={close}
        disabled={formDisabled}
        defaultFooter={{
          generalError: form.formState.errors.root?.message,
          cancelButton: {
            ...entry.options.cancelButton,
            onClick: cancel,
            disabled: formDisabled || entry.options.cancelButton?.disabled,
          },
          confirmButton: {
            ...entry.options.confirmButton,
            disabled: formDisabled || entry.options.confirmButton?.disabled,
            isLoading: form.formState.isSubmitting,
            hasLoaded: form.formState.isSubmitSuccessful,
            onAnimationComplete: () => {
              resolveAnimationComplete.current?.();
              resolveAnimationComplete.current = undefined;
            },
            onClick: form.handleSubmit(async (payload) => {
              try {
                const animationPromise = new Promise<void>((resolve) => {
                  resolveAnimationComplete.current = resolve;
                });
                await entry.options.submitHandler?.(payload, formApi);
                void animationPromise.then(() => confirm(payload));
              } catch (error) {
                if (isValidationProblemDetails(error)) {
                  handleValidationError(error);
                  return;
                }

                form.setError('root', { type: 'server', message: t('Common_UnexpectedError') });
              }
            }),
          },
        }}
        renderFooter={entry.options.renderFooter?.({ form: formApi, confirm, cancel, close })}
      >
        {entry.options.renderBody({ control: form.control })}
      </ModalFrame>
    </FormProvider>
  );
};
